import React from 'react';
import {
  AlertTriangle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { HistoryEntry, SkippedRow } from './types';
import { formatCurrencyBRL } from './utils';

type HistoryModalProps = {
  isOpen: boolean;
  loading?: boolean;
  history: HistoryEntry[];
  onClose: () => void;
};

type HistoryFilter = 'all' | 'changes' | 'skipped';

function formatValue(value: any) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') return formatCurrencyBRL(value);
  return String(value);
}

function isSkippedEntry(entry: HistoryEntry) {
  return entry.log_name === 'metas-unilever-skipped' || (entry.data?.skipped?.length || 0) > 0;
}

function skippedReasonText(row: SkippedRow) {
  const reasons = row.reasons || [];
  if (reasons.length === 0) return 'Nao identificado nas dimensoes de referencia';
  return reasons.map((reason) => reason.message).join(' | ');
}

const ChangeRows: React.FC<{ entry: HistoryEntry }> = ({ entry }) => {
  const changes = entry.data?.changes || [];

  return (
    <div className="divide-y divide-gray-100 border-t border-gray-100 bg-gray-50/50">
      {changes.map((change, index) => (
        <div key={`${change.guid}-${index}`} className="px-5 py-4">
          <div className="mb-3 flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
              change.action === 'insert'
                ? 'bg-emerald-50 text-emerald-700'
                : change.action === 'delete'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-blue-50 text-blue-700'
            }`}>
              {change.action === 'insert'
                ? <Plus className="h-3 w-3" />
                : change.action === 'delete'
                  ? <Trash2 className="h-3 w-3" />
                  : <RefreshCw className="h-3 w-3" />}
              {change.action === 'insert' ? 'Novo' : change.action === 'delete' ? 'Removido' : 'Alterado'}
            </span>
            <p className="truncate text-sm font-semibold text-gray-900">{change.distribuidor}</p>
          </div>

          <div className="space-y-2">
            {(change.fields || []).map((field) => (
              <div key={`${change.guid}-${field.field}`} className="grid gap-2 rounded-lg bg-white p-3 text-xs sm:grid-cols-[140px_1fr_auto_1fr]">
                <span className="font-semibold text-gray-500">{field.label}</span>
                <span className="min-w-0 truncate rounded-md border border-red-100 bg-red-50 px-2 py-1 text-red-700">
                  {formatValue(field.before)}
                </span>
                <ArrowRight className="hidden h-4 w-4 self-center text-gray-400 sm:block" />
                <span className="min-w-0 truncate rounded-md border border-emerald-100 bg-emerald-50 px-2 py-1 text-emerald-700">
                  {formatValue(field.after)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const SkippedRows: React.FC<{ entry: HistoryEntry }> = ({ entry }) => {
  const skipped = entry.data?.skipped || [];

  return (
    <div className="divide-y divide-amber-100 border-t border-amber-100 bg-amber-50/40">
      {skipped.map((row, index) => (
        <div key={`${row.row}-${row.distribuidor}-${index}`} className="px-5 py-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
              <AlertTriangle className="h-3 w-3" />
              Ignorado
            </span>
            <p className="text-sm font-semibold text-gray-900">Linha {row.row}</p>
          </div>

          <div className="grid gap-2 rounded-lg bg-white p-3 text-xs sm:grid-cols-[130px_1fr]">
            <span className="font-semibold text-gray-500">Distribuidor</span>
            <span className="min-w-0 break-words text-gray-800">{row.distribuidor || '-'}</span>
            <span className="font-semibold text-gray-500">Regional</span>
            <span className="min-w-0 break-words text-gray-800">{row.regional || '-'}</span>
            <span className="font-semibold text-gray-500">Motivo</span>
            <span className="min-w-0 break-words text-amber-800">{skippedReasonText(row)}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const HistoryCard: React.FC<{ entry: HistoryEntry }> = ({ entry }) => {
  const [open, setOpen] = React.useState(false);
  const changes = entry.data?.changes || [];
  const skipped = entry.data?.skipped || [];
  const date = new Date(entry.created_at);
  const skippedLog = isSkippedEntry(entry);

  return (
    <div className={`overflow-hidden rounded-xl border bg-white ${skippedLog ? 'border-amber-200' : 'border-gray-200'}`}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex min-w-0 items-center gap-4">
          <div className={`rounded-lg p-2 ${skippedLog ? 'bg-amber-50' : 'bg-blue-50'}`}>
            {skippedLog
              ? <AlertTriangle className="h-4 w-4 text-amber-700" />
              : <Clock className="h-4 w-4 text-blue-600" />}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              {date.toLocaleDateString('pt-BR')} <span className="font-normal text-gray-400">{date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
            </p>
            <div className="mt-1 flex items-center gap-1.5">
              <User className="h-3 w-3 text-gray-400" />
              <p className="truncate text-xs text-gray-500">{entry.name || entry.email}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
            skippedLog ? 'bg-amber-100 text-amber-800' : 'bg-blue-50 text-blue-700'
          }`}>
            {skippedLog
              ? `${skipped.length} ${skipped.length === 1 ? 'ignorado' : 'ignorados'}`
              : `${changes.length} ${changes.length === 1 ? 'registro' : 'registros'}`}
          </span>
          {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {open && (skippedLog ? <SkippedRows entry={entry} /> : <ChangeRows entry={entry} />)}
    </div>
  );
};

export const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  loading = false,
  history,
  onClose,
}) => {
  const [visible, setVisible] = React.useState(false);
  const [filter, setFilter] = React.useState<HistoryFilter>('all');

  React.useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const totalChanges = history.reduce((sum, entry) => sum + (entry.data?.changes?.length || 0), 0);
  const totalSkipped = history.reduce((sum, entry) => sum + (entry.data?.skipped?.length || 0), 0);
  const filteredHistory = history.filter((entry) => {
    if (filter === 'changes') return !isSkippedEntry(entry);
    if (filter === 'skipped') return isSkippedEntry(entry);
    return true;
  });

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 transition-all duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Historico de metas Unilever"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div
        className={`relative flex max-h-[88vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl transition-all duration-200 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-3'
        }`}
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Historico de Alteracoes</h2>
              <p className="mt-0.5 text-xs text-gray-500">
                {history.length} {history.length === 1 ? 'log' : 'logs'} | {totalChanges} alteracoes | {totalSkipped} ignorados
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-4 inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1">
            {[
              { key: 'all', label: 'Todos' },
              { key: 'changes', label: 'Alteracoes' },
              { key: 'skipped', label: 'Ignorados' },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setFilter(item.key as HistoryFilter)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  filter === item.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="mb-3 h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm text-gray-500">Carregando historico...</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="mb-3 rounded-full bg-gray-100 p-4">
                <Clock className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500">Nenhum historico disponivel</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredHistory.map((entry) => (
                <HistoryCard key={entry.id_log} entry={entry} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
